'use client';

import { useCallback, useMemo } from 'react';
import { isServer } from '@frameio/utils';
import { ActiveHostEntitiesContext } from '../../shared/contexts/ActiveHostEntitiesContext';
import {
  useGetActiveAfterEffectsEntitiesQuery,
  useGetActiveCompositionQuery,
} from '../gql';
import {
  CompositionExportContextProvider,
  useIsCompositionRendering,
} from './CompositionExportContext';
import { CompositionExportProgressProvider } from './CompositionExportProgressContext';

const ACTIVE_COMPOSITION_POLL_INTERVAL_MS = 200;

function ActiveCompositionPoller() {
  const isRendering = useIsCompositionRendering();

  useGetActiveCompositionQuery({
    context: { resolveWithUXP: true },
    pollInterval: isRendering ? 0 : ACTIVE_COMPOSITION_POLL_INTERVAL_MS,
    skip: isServer || isRendering,
  });

  return null;
}

export function ActiveAfterEffectsEntitiesProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data, refetch } = useGetActiveAfterEffectsEntitiesQuery({
    context: { resolveWithUXP: true },
    skip: isServer,
  });

  const refetchActiveEntities = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const activeHostEntitiesValue = useMemo(
    () => ({
      projectId: data?.activeAfterEffectsProject?.id ?? null,
      documentId: data?.activeComposition?.id ?? null,
      documentName: data?.activeComposition?.name ?? null,
      refetchActiveEntities,
    }),
    [data, refetchActiveEntities]
  );

  return (
    <ActiveHostEntitiesContext.Provider value={activeHostEntitiesValue}>
      <CompositionExportContextProvider>
        <ActiveCompositionPoller />
        <CompositionExportProgressProvider>
          {children}
        </CompositionExportProgressProvider>
      </CompositionExportContextProvider>
    </ActiveHostEntitiesContext.Provider>
  );
}
